import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  Mail,
  TrendingUp,
  Gift,
  Calendar,
  Send,
  Target,
  UserCheck,
  Clock,
  Star,
  Filter,
  Download,
  Plus,
  CheckCircle,
  Paperclip,
  Image,
  X,
  FileText,
  Upload,
  Palette,
  Share2
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import DigitalMarketingToolkit from "./DigitalMarketingToolkit";

const MarketingDashboard = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState("all");
  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: "",
    segment: "all"
  });
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchMarketingData();
  }, [token]);

  const fetchMarketingData = async () => {
    try {
      const [customersRes, statsRes] = await Promise.all([
        axios.get(`${API}/marketing/customers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/marketing/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCustomers(customersRes.data?.customers || []);
      setStats(statsRes.data || {});
      
      // Generate segments from customer data
      generateSegments(customersRes.data?.customers || []);
    } catch (error) {
      console.error("Failed to fetch marketing data:", error);
      // Set demo data if API fails
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const setDemoData = () => {
    setStats({
      total_customers: 0,
      active_customers: 0,
      new_this_month: 0,
      average_visits: 0,
      vip_customers: 0,
      at_risk: 0
    });
    setSegments([
      { id: "all", name: "All Customers", count: 0, icon: Users },
      { id: "vip", name: "VIP (Gold/Platinum)", count: 0, icon: Star },
      { id: "active", name: "Active (Last 30 days)", count: 0, icon: UserCheck },
      { id: "at_risk", name: "At Risk (60+ days)", count: 0, icon: Clock },
      { id: "new", name: "New (This Month)", count: 0, icon: Plus }
    ]);
  };

  const generateSegments = (customerList) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now - 60 * 24 * 60 * 60 * 1000);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const segmentData = [
      { 
        id: "all", 
        name: "All Customers", 
        count: customerList.length,
        icon: Users 
      },
      { 
        id: "vip", 
        name: "VIP (Gold/Platinum)", 
        count: customerList.filter(c => ["Gold", "Platinum"].includes(c.tier)).length,
        icon: Star 
      },
      { 
        id: "active", 
        name: "Active (Last 30 days)", 
        count: customerList.filter(c => new Date(c.last_visit) >= thirtyDaysAgo).length,
        icon: UserCheck 
      },
      { 
        id: "at_risk", 
        name: "At Risk (60+ days)", 
        count: customerList.filter(c => new Date(c.last_visit) < sixtyDaysAgo).length,
        icon: Clock 
      },
      { 
        id: "new", 
        name: "New (This Month)", 
        count: customerList.filter(c => new Date(c.created_at) >= startOfMonth).length,
        icon: Plus 
      }
    ];

    setSegments(segmentData);
  };

  const getFilteredCustomers = () => {
    if (selectedSegment === "all") return customers;
    
    const now = new Date();
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now - 60 * 24 * 60 * 60 * 1000);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    switch (selectedSegment) {
      case "vip":
        return customers.filter(c => ["Gold", "Platinum"].includes(c.tier));
      case "active":
        return customers.filter(c => new Date(c.last_visit) >= thirtyDaysAgo);
      case "at_risk":
        return customers.filter(c => new Date(c.last_visit) < sixtyDaysAgo);
      case "new":
        return customers.filter(c => new Date(c.created_at) >= startOfMonth);
      default:
        return customers;
    }
  };

  const sendCampaign = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error("Please fill in subject and message");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('subject', emailForm.subject);
      formData.append('message', emailForm.message);
      formData.append('segment', emailForm.segment);
      formData.append('recipients', JSON.stringify(getFilteredCustomers().map(c => c.email)));
      
      // Add attachments
      attachments.forEach((attachment, index) => {
        if (attachment.file) {
          formData.append(`attachment_${index}`, attachment.file);
        }
      });

      await axios.post(`${API}/marketing/campaign`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Campaign sent to ${getFilteredCustomers().length} customers!`);
      setEmailForm({ subject: "", message: "", segment: "all" });
      setAttachments([]);
    } catch (error) {
      toast.error("Failed to send campaign");
    }
  };

  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    const maxTotal = 10; // Max 10 attachments
    
    if (attachments.length + files.length > maxTotal) {
      toast.error(`Maximum ${maxTotal} attachments allowed`);
      return;
    }

    const newAttachments = files.map(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} exceeds 5MB limit`);
        return null;
      }
      
      const isImage = file.type.startsWith('image/');
      return {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        type: isImage ? 'image' : 'file',
        preview: isImage ? URL.createObjectURL(file) : null
      };
    }).filter(Boolean);

    setAttachments(prev => [...prev, ...newAttachments]);
    
    // Reset input
    if (e.target) e.target.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(a => a.id !== id);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const exportCustomers = () => {
    const data = getFilteredCustomers();
    const csv = [
      ["Name", "Email", "Phone", "Tier", "Total Visits", "Last Visit", "Total Spent"].join(","),
      ...data.map(c => [
        c.name,
        c.email,
        c.phone || "",
        c.tier || "Bronze",
        c.total_visits || 0,
        c.last_visit || "",
        c.total_spent || 0
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers_${selectedSegment}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("Customer list exported!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Marketing & CRM</h2>
        <p className="text-gray-500">Manage customer relationships and marketing campaigns</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.total_customers || 0}</p>
            <p className="text-xs text-gray-500">Total Customers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <UserCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.active_customers || 0}</p>
            <p className="text-xs text-gray-500">Active (30 days)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Plus className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.new_this_month || 0}</p>
            <p className="text-xs text-gray-500">New This Month</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Star className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.vip_customers || 0}</p>
            <p className="text-xs text-gray-500">VIP Members</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.at_risk || 0}</p>
            <p className="text-xs text-gray-500">At Risk</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 text-orange-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.average_visits?.toFixed(1) || 0}</p>
            <p className="text-xs text-gray-500">Avg Visits</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="toolkit" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="toolkit" className="flex items-center gap-1">
            <Palette className="w-4 h-4" />
            Social Cards
          </TabsTrigger>
          <TabsTrigger value="segments">Customer Segments</TabsTrigger>
          <TabsTrigger value="campaign">Email Campaign</TabsTrigger>
          <TabsTrigger value="list">Customer List</TabsTrigger>
        </TabsList>

        {/* Digital Marketing Toolkit Tab */}
        <TabsContent value="toolkit">
          <DigitalMarketingToolkit token={token} />
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {segments.map((segment) => {
              const Icon = segment.icon;
              return (
                <Card 
                  key={segment.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedSegment === segment.id 
                      ? "ring-2 ring-orange-500 bg-orange-50" 
                      : ""
                  }`}
                  onClick={() => setSelectedSegment(segment.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          segment.id === "vip" ? "bg-amber-100" :
                          segment.id === "active" ? "bg-green-100" :
                          segment.id === "at_risk" ? "bg-red-100" :
                          segment.id === "new" ? "bg-purple-100" :
                          "bg-blue-100"
                        }`}>
                          <Icon className={`w-6 h-6 ${
                            segment.id === "vip" ? "text-amber-600" :
                            segment.id === "active" ? "text-green-600" :
                            segment.id === "at_risk" ? "text-red-600" :
                            segment.id === "new" ? "text-purple-600" :
                            "text-blue-600"
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">{segment.name}</h3>
                          <p className="text-2xl font-bold text-gray-900">{segment.count}</p>
                        </div>
                      </div>
                      {selectedSegment === segment.id && (
                        <CheckCircle className="w-6 h-6 text-orange-500" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="mt-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-600">
                  <span className="font-semibold">{getFilteredCustomers().length}</span> customers in selected segment
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={exportCustomers}
                    disabled={getFilteredCustomers().length === 0}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button 
                    className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                    disabled={getFilteredCustomers().length === 0}
                    onClick={() => document.querySelector('[data-state="inactive"][value="campaign"]')?.click()}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Campaign
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaign Tab */}
        <TabsContent value="campaign">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-orange-500" />
                Create Email Campaign
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-700">
                  <strong>Target Audience:</strong> {segments.find(s => s.id === selectedSegment)?.name} ({getFilteredCustomers().length} customers)
                </p>
              </div>

              <div>
                <Label>Email Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                  placeholder="e.g., Special Offer Just for You!"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Message</Label>
                <Textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                  placeholder="Write your promotional message here..."
                  rows={6}
                  className="mt-1"
                />
              </div>

              {/* Attachments Section */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </Label>
                
                {/* Hidden file inputs */}
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={(e) => handleFileSelect(e, 'image')}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileSelect(e, 'file')}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  multiple
                  className="hidden"
                />

                {/* Attachment buttons */}
                <div className="flex gap-2 mb-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imageInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <Image className="w-4 h-4 text-blue-500" />
                    Add Photos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4 text-green-500" />
                    Add Files
                  </Button>
                </div>

                {/* Attachment previews */}
                {attachments.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex flex-wrap gap-3">
                      {attachments.map((attachment) => (
                        <div 
                          key={attachment.id}
                          className="relative group"
                        >
                          {attachment.type === 'image' ? (
                            <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-300">
                              <img 
                                src={attachment.preview} 
                                alt={attachment.name}
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => removeAttachment(attachment.id)}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="relative flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg">
                              <FileText className="w-5 h-5 text-gray-400" />
                              <div className="max-w-[100px]">
                                <p className="text-xs font-medium text-gray-700 truncate">{attachment.name}</p>
                                <p className="text-xs text-gray-400">{formatFileSize(attachment.size)}</p>
                              </div>
                              <button
                                onClick={() => removeAttachment(attachment.id)}
                                className="ml-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {attachments.length}/10 attachments • Max 5MB per file
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Available Merge Tags:</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">{"{{name}}"}</Badge>
                  <Badge variant="secondary">{"{{tier}}"}</Badge>
                  <Badge variant="secondary">{"{{points}}"}</Badge>
                  <Badge variant="secondary">{"{{last_visit}}"}</Badge>
                </div>
              </div>

              <Button 
                onClick={sendCampaign}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                disabled={!emailForm.subject || !emailForm.message || getFilteredCustomers().length === 0}
              >
                <Send className="w-4 h-4 mr-2" />
                Send to {getFilteredCustomers().length} Customers
                {attachments.length > 0 && ` (${attachments.length} attachment${attachments.length > 1 ? 's' : ''})`}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer List Tab */}
        <TabsContent value="list">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Customer List</CardTitle>
              <Button variant="outline" onClick={exportCustomers}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {getFilteredCustomers().length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">No Customers Found</h3>
                  <p className="text-gray-400">Customers will appear here after their first appointment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-3 px-4 font-medium text-gray-600">Customer</th>
                        <th className="py-3 px-4 font-medium text-gray-600">Contact</th>
                        <th className="py-3 px-4 font-medium text-gray-600">Tier</th>
                        <th className="py-3 px-4 font-medium text-gray-600">Visits</th>
                        <th className="py-3 px-4 font-medium text-gray-600">Last Visit</th>
                        <th className="py-3 px-4 font-medium text-gray-600">Total Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredCustomers().slice(0, 50).map((customer) => (
                        <tr key={customer.user_id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-800">{customer.name}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600">{customer.email}</p>
                            {customer.phone && (
                              <p className="text-xs text-gray-400">{customer.phone}</p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={
                              customer.tier === "Platinum" ? "bg-purple-100 text-purple-700" :
                              customer.tier === "Gold" ? "bg-amber-100 text-amber-700" :
                              customer.tier === "Silver" ? "bg-gray-200 text-gray-700" :
                              "bg-orange-100 text-orange-700"
                            }>
                              {customer.tier || "Bronze"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {customer.total_visits || 0}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {customer.last_visit 
                              ? new Date(customer.last_visit).toLocaleDateString()
                              : "Never"
                            }
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-800">
                            ${(customer.total_spent || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {getFilteredCustomers().length > 50 && (
                    <p className="text-center text-gray-500 text-sm mt-4">
                      Showing 50 of {getFilteredCustomers().length} customers. Export for full list.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketingDashboard;
